import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, MapPin, User, LogOut, ChevronRight, Clock, ExternalLink } from 'lucide-react';
// import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './UserDashboard.css';

export default function UserDashboard() {
  const { user, logout, refreshUser, API_BASE } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('orders');
  
  // Address Modal State
  const [showAddrModal, setShowAddrModal] = useState(false);
  const [editAddrId, setEditAddrId] = useState(null);
  const [addrForm, setAddrForm] = useState({
    firstName: '', lastName: '', address: '', city: '', pincode: '', phone: '', isDefault: false
  });
  const [savingAddr, setSavingAddr] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }

    const fetchOrders = async () => {
      try {
        const token = localStorage.getItem('koa_token');
        const res = await fetch(`${API_BASE}/orders/my`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok) setOrders(data);
      } catch (err) {
        console.error('Failed to fetch orders');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [user, navigate, API_BASE]);

  const handleEditClick = (addr) => {
    setEditAddrId(addr._id);
    setAddrForm({ ...addr });
    setShowAddrModal(true);
  };

  const handleDeleteAddress = async (id) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;
    try {
      const token = localStorage.getItem('koa_token');
      await fetch(`${API_BASE}/auth/address/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshUser();
    } catch (err) {
      alert('Error deleting address');
    }
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    setSavingAddr(true);
    try {
      const token = localStorage.getItem('koa_token');
      const url = editAddrId ? `${API_BASE}/auth/address/${editAddrId}` : `${API_BASE}/auth/address`;
      const method = editAddrId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(addrForm)
      });

      if (res.ok) {
        await refreshUser();
        setShowAddrModal(false);
        setEditAddrId(null);
        setAddrForm({ firstName: '', lastName: '', address: '', city: '', pincode: '', phone: '', isDefault: false });
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to save address');
      }
    } catch (err) {
      alert('Error saving address');
    } finally {
      setSavingAddr(false);
    }
  };

  if (!user) return null;

  return (
    <main className="dashboard-page container anim-fade-up">
      <div className="dashboard-layout">
        <aside className="dashboard-sidebar">
          <div className="user-profile-mini">
            <div className="user-avatar">{user.name[0]}</div>
            <div className="user-info"><h3>{user.name}</h3><p>{user.email || user.mobile}</p></div>
          </div>
          <nav className="dashboard-nav">
            <button className={`nav-item ${activeTab === 'orders' ? 'active' : ''}`} onClick={() => setActiveTab('orders')}><Package size={18} /> My Orders</button>
            <button className={`nav-item ${activeTab === 'addresses' ? 'active' : ''}`} onClick={() => setActiveTab('addresses')}><MapPin size={18} /> Saved Addresses</button>
            <button className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => setActiveTab('profile')}><User size={18} /> Profile Settings</button>
            <button className="nav-item logout" onClick={logout}><LogOut size={18} /> Logout</button>
          </nav>
        </aside>

        <section className="dashboard-content">
          {activeTab === 'orders' ? (
            <>
              <div className="content-header"><h1>My Orders</h1><p>View and track your previous orders</p></div>
              {loading ? <div className="loading-state">Loading your orders...</div> : orders.length === 0 ? (
                <div className="empty-orders"><Package size={48} strokeWidth={1} /><p>No orders yet</p><button className="btn-primary" onClick={() => navigate('/shop')}>Start Shopping</button></div>
              ) : (
                <div className="orders-list">
                  {orders.map(order => (
                    <div key={order._id} className="order-card-mini">
                      <div className="order-card-header"><div className="order-id-wrap"><p className="order-id">#{order.orderId}</p><p className="order-date">{new Date(order.createdAt).toLocaleDateString()}</p></div><div className="order-status-badge" data-status={order.status}>{order.status.replace(/_/g, ' ')}</div></div>
                      <div className="order-card-body"><div className="order-items-preview">{order.items.slice(0, 3).map((item, i) => (<img key={i} src={item.image} alt={item.name} className="preview-img" title={item.name} />))}{order.items.length > 3 && <span className="more-items">+{order.items.length - 3}</span>}</div><div className="order-total-mini"><p>Total</p><h4>₹{order.totals.total}</h4></div></div>
                      <div className="order-card-footer"><button className="btn-text" onClick={() => navigate(`/track-order?id=${order.orderId}`)}>Track Order <ChevronRight size={14} /></button><button className="btn-outline-sm">Reorder</button></div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : activeTab === 'addresses' ? (
            <>
              <div className="content-header"><h1>Saved Addresses</h1><p>Manage your delivery locations</p></div>
              <div className="address-grid">
                <button className="add-address-card" onClick={() => { setEditAddrId(null); setAddrForm({ firstName: '', lastName: '', address: '', city: '', pincode: '', phone: '', isDefault: false }); setShowAddrModal(true); }}>
                  <div className="add-icon">+</div><p>Add New Address</p>
                </button>
                {user.addresses?.map((addr, i) => (
                  <div key={i} className="address-card">
                    {addr.isDefault && <span className="default-tag">Default</span>}
                    <h4>{addr.firstName} {addr.lastName}</h4><p>{addr.address}</p><p>{addr.city}, {addr.pincode}</p>
                    <div className="addr-actions">
                      <button className="btn-text" onClick={() => handleEditClick(addr)}>Edit</button>
                      <button className="btn-text delete" onClick={() => handleDeleteAddress(addr._id)}>Remove</button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="content-header"><h1>Profile Settings</h1><p>Update your personal information</p></div>
              <form className="profile-form">
                <div className="form-grid"><div className="form-group"><label>Full Name</label><input type="text" defaultValue={user.name} /></div><div className="form-group"><label>Email</label><input type="email" defaultValue={user.email} /></div><div className="form-group"><label>Mobile</label><input type="tel" defaultValue={user.mobile} disabled /></div></div>
                <button type="submit" className="btn-primary" style={{ marginTop: '20px' }}>Save Changes</button>
              </form>
            </>
          )}
        </section>
      </div>

      {showAddrModal && (
        <div className="modal-overlay">
          <div className="modal-content address-modal anim-fade-up">
            <div className="modal-header"><h3>{editAddrId ? 'Edit Address' : 'Add New Address'}</h3><button className="close-btn" onClick={() => setShowAddrModal(false)}>×</button></div>
            <form onSubmit={handleSaveAddress} className="address-form-content">
              <div className="form-grid">
                <div className="form-group"><input type="text" placeholder="First Name" required value={addrForm.firstName} onChange={e => setAddrForm({...addrForm, firstName: e.target.value})} /></div>
                <div className="form-group"><input type="text" placeholder="Last Name" required value={addrForm.lastName} onChange={e => setAddrForm({...addrForm, lastName: e.target.value})} /></div>
                <div className="form-group full"><input type="text" placeholder="Full Address" required value={addrForm.address} onChange={e => setAddrForm({...addrForm, address: e.target.value})} /></div>
                <div className="form-group"><input type="text" placeholder="City" required value={addrForm.city} onChange={e => setAddrForm({...addrForm, city: e.target.value})} /></div>
                <div className="form-group"><input type="text" placeholder="Pincode" required value={addrForm.pincode} onChange={e => setAddrForm({...addrForm, pincode: e.target.value})} /></div>
                <div className="form-group full"><input type="tel" placeholder="Mobile Number" required value={addrForm.phone} onChange={e => setAddrForm({...addrForm, phone: e.target.value})} /></div>
              </div>
              <div className="form-footer"><button type="button" className="btn-text" onClick={() => setShowAddrModal(false)}>Cancel</button><button type="submit" className="btn-primary" disabled={savingAddr}>{savingAddr ? 'Saving...' : 'Save Address'}</button></div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
}
